import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignIn } from "@assets/images";
import { CountryPhoneInputValue, FormHelper } from "@utils/FormHelper";
import { usePrevious } from "@utils/CustomHooks";
import { displayErrorMessage } from "@utils/DisplayMessageUtility";
import { getLoggedInUserId } from "@utils/UserUtils";
import Navigator from "@navigator/index";
import { NavigationUrl, URLs } from "@navigator/NavigationUrl";
import { translate } from "@translations/Translater";
import { IProfile, ProfileModel } from "@common/domain/models/Profile";
import { IUpdateSettingPayload } from "@common/domain/models/SettingsConfig";
import { dashboardMessages } from "@containers/Dashboard/messages";
import { PrimaryButton } from '@components/Button';
import Text, { FontFamilyVariant, TextType } from '@components/Text';
import { PLACEMENT_STATUS, POSTREGISTRATION_SCREENS, calculateDaysSinceRegistration, duration, getDisplayStatus, getRedirectScreen, isUserCreatedBeforeRelease, showForcedPopup } from "@presentation/Profile/components/ProfileFields/utils";
import AuthForm from "@presentation/auth/components/AuthForm";
import Shimmer from "@components/atoms/Shimmer";
import { WFSpinner } from "@components/index";
import BasicInfo, { IPhoneNumber } from "./components/BasicInfo";
import { presentStatusKeys, typeOfEmploymentKeys } from "@constants/config";

import "./styles.less";


const EducationEmploymentInfo = React.lazy(() => import(/* webpackChunkName: "EducationEmploymentInfo" */'./components/EducationEmploymentInfo'));
const EducationEmploymentInfoPhase2 = React.lazy(() => import(/* webpackChunkName: "EducationEmploymentInfoPhase2" */'./components/EducationEmploymentInfoP2'));

export interface IBasicInfo {
    gender: string,
    phoneNumber: CountryPhoneInputValue,
    enableWhatsappNotification: boolean,
    yearOfBirth: string,
    phone: IPhoneNumber
}

export interface IPresentStatus {
    isStudent: number;
    isEmployed: number;
    presentStatus: string;
}

export interface IEducationalDetails {
    degree: string;
    graduationDate: string;
    aspiringSector: [];
    instituteName: string;
}

export interface IEmploymentDetails {
    employerName: string;
    employmentSector: string;
    startDate: string;
    salary: {
        currency: string,
        amount: number,
        duration: string,
    },
    employmentType: string,
    documents: string,
    description: string,
    selfEmployedDetails: {
        employeesCount: number,
        revenue: {
            currency: string,
            amount: number,
            duration: string,
        }
    }
}

export interface IUnemployedDetails {
    aspiringSector: []
}

interface IProps {
    userProfile: ProfileModel;
    updateProfileError: string;
    isProfileUpdating: boolean;
    updateProfile: (profile: IProfile, userId: string) => void;
    fetchUserInstitute: (payload) => void;
    updateUserSettings: (payload) => void;
}
const ProfileCompletion: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const { isProfileUpdating, updateProfile, updateProfileError, userProfile, fetchUserInstitute, updateUserSettings } = props;
    const [currentSlide, setCurrentSlide] = useState(null);
    const [basicInfoData, setbasicInfoData] = useState<IBasicInfo>({} as IBasicInfo);
    const [presentStatusData, setPresentStatusData] = useState<IPresentStatus>({} as IPresentStatus);
    const [educationInfoData, setEducationInfoData] = useState<IEducationalDetails>({} as IEducationalDetails);
    const [employmentInfoData, setEmploymentInfoData] = useState<IEmploymentDetails>({} as IEmploymentDetails);
    const [unEmployedDetail, setUnEmployedDetail] = useState<IUnemployedDetails>({} as IUnemployedDetails);
    const [hideDoLater, setHideDoLater] = useState(false);
    const [showNudgeForPhase2, setShowNudgeForPhase2] = useState(false);

    const prevIsProfileUpdating = usePrevious(isProfileUpdating);

    useEffect(() => {
        fetchUserInstitute({ studentId: getLoggedInUserId() });
    }, []);

    const onClickDoLater = () => {
        Navigator.push(NavigationUrl.generate(URLs.home));
    }
    const onClickCompleteProfile = () => {
        const currentDate = new Date();
        if (currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS) {
            // @ts-ignore
            const payload: IProfile = {
                firstName: userProfile?.getFirstName(),
                lastName: userProfile?.getLastname(),
                email: userProfile?.getEmail(),
                preferredLanguage: userProfile?.getPreferredLanguage(),
                ...(basicInfoData?.phone?.number?.length > 0 && {
                    countryCode: `+${basicInfoData?.phone.countryCode}` || null,
                    phoneNumber: basicInfoData?.phone.number || null,
                }),
                ...(basicInfoData?.gender?.length > 0 && {
                    gender: basicInfoData?.gender
                }),
                ...(basicInfoData?.yearOfBirth?.length > 0 && {
                    yearOfBirth: basicInfoData?.yearOfBirth
                }),
                ...(basicInfoData?.enableWhatsappNotification && {
                    enableWhatsappNotification: basicInfoData?.enableWhatsappNotification
                })
            }
            updateProfile(payload, userProfile?.getUserId());
        }

        else if (currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) {
            if (presentStatusData?.isEmployed === 2 && presentStatusData?.isStudent === 2) {
                // @ts-ignore
                const EmploymentEducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    ...(unEmployedDetail?.aspiringSector?.length > 0 && { aspiringSectors: unEmployedDetail?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS.UNEMPLOYED,
                }
                updateProfile(EmploymentEducationalDetailsPayload, userProfile?.getUserId());
            }
            else if (presentStatusData?.isEmployed === 1 && presentStatusData?.isStudent === 1) {
                // @ts-ignore
                const EmploymentEducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    ...(educationInfoData?.aspiringSector?.length > 0 && { aspiringSectors: educationInfoData?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS?.STUDENT_EMPLOYED,
                    ...(educationInfoData?.aspiringSector?.length > 0 && {
                        educationDetails: {
                            degree: educationInfoData?.degree,
                            graduationDate: educationInfoData?.graduationDate,
                            instituteName: educationInfoData?.instituteName,
                        }
                    }),
                    ...(employmentInfoData?.employmentSector?.length > 0 && {
                        employmentDetails: {
                            employerName: employmentInfoData?.employerName,
                            employmentSector: employmentInfoData?.employmentSector,
                            startDate: employmentInfoData?.startDate
                        }
                    })
                }
                updateProfile(EmploymentEducationalDetailsPayload, userProfile?.getUserId());
            }
            else if (presentStatusData?.isStudent === 1) {
                // @ts-ignore
                const EducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    ...(educationInfoData?.aspiringSector?.length > 0 && { aspiringSectors: educationInfoData?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS.STUDENT,
                    ...(educationInfoData?.aspiringSector?.length > 0 && {
                        educationDetails: {
                            degree: educationInfoData?.degree,
                            graduationDate: educationInfoData?.graduationDate,
                            instituteName: educationInfoData?.instituteName
                        }
                    })
                }
                updateProfile(EducationalDetailsPayload, userProfile?.getUserId());
            }

            else if (presentStatusData?.isEmployed === 1) {
                // @ts-ignore
                const EmploymentDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    placementStatus: PLACEMENT_STATUS.EMPLOYED,
                    ...(employmentInfoData?.employmentSector?.length > 0 && {
                        employmentDetails: {
                            employerName: employmentInfoData?.employerName,
                            employmentSector: employmentInfoData?.employmentSector,
                            startDate: employmentInfoData?.startDate
                        }
                    })
                }
                updateProfile(EmploymentDetailsPayload, userProfile?.getUserId());
            }
        } else if (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) {
            if (presentStatusData?.isEmployed === 2 && presentStatusData?.isStudent === 2) {
                // @ts-ignore
                const EmploymentEducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    ...(unEmployedDetail?.aspiringSector?.length > 0 && { aspiringSectors: unEmployedDetail?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS.UNEMPLOYED,
                    educationorEmployementDataUpdatedOn: currentDate.toString(),
                    educationOrEmploymentType: presentStatusData?.presentStatus
                }
                updateProfile(EmploymentEducationalDetailsPayload, userProfile?.getUserId());
            }
            else if (presentStatusData?.isEmployed === 1 && presentStatusData?.isStudent === 1) {
                // @ts-ignore
                const EmploymentEducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    educationorEmployementDataUpdatedOn: currentDate.toString(),
                    educationOrEmploymentType: presentStatusData?.presentStatus,
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    ...(educationInfoData?.aspiringSector?.length > 0 && { aspiringSectors: educationInfoData?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS?.STUDENT_EMPLOYED,
                    ...(educationInfoData?.aspiringSector?.length > 0 && {
                        educationDetails: {
                            degree: educationInfoData?.degree,
                            graduationDate: educationInfoData?.graduationDate,
                            instituteName: educationInfoData?.instituteName
                        }
                    }),
                    ...(employmentInfoData?.employmentSector?.length > 0 && {
                        employmentDetails: {
                            employerName: employmentInfoData?.employerName,
                            employmentSector: employmentInfoData?.employmentSector,
                            startDate: employmentInfoData?.startDate
                        }
                    })
                }
                updateProfile(EmploymentEducationalDetailsPayload, userProfile?.getUserId());
            }
            else if (presentStatusData?.isStudent === 1) {
                // @ts-ignore
                const EducationalDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    educationorEmployementDataUpdatedOn: currentDate.toString(),
                    educationOrEmploymentType: presentStatusData?.presentStatus,
                    ...(educationInfoData?.aspiringSector?.length > 0 && { aspiringSectors: educationInfoData?.aspiringSector }),
                    placementStatus: PLACEMENT_STATUS.STUDENT,
                    ...(educationInfoData?.aspiringSector?.length > 0 && {
                        educationDetails: {
                            degree: educationInfoData?.degree,
                            graduationDate: educationInfoData?.graduationDate,
                            instituteName: educationInfoData?.instituteName
                        }
                    })
                }
                updateProfile(EducationalDetailsPayload, userProfile?.getUserId());
            }

            else if (presentStatusData?.isEmployed === 1) {
                // @ts-ignore
                const EmploymentDetailsPayload: IProfile = {
                    firstName: userProfile?.getFirstName(),
                    lastName: userProfile?.getLastname(),
                    email: userProfile?.getEmail(),
                    preferredLanguage: userProfile?.getPreferredLanguage(),
                    educationorEmployementDataUpdatedOn: currentDate.toString(),
                    educationOrEmploymentType: presentStatusData?.presentStatus,
                    placementStatus: PLACEMENT_STATUS.EMPLOYED,
                    ...((employmentInfoData?.employmentSector?.length > 0 || employmentInfoData?.salary?.amount) && {
                        employmentDetails: {
                            ...(employmentInfoData?.startDate && { startDate: employmentInfoData?.startDate }),
                            ...(employmentInfoData?.employerName && { employerName: employmentInfoData?.employerName }),
                            ...(employmentInfoData?.employmentSector && { employmentSector: employmentInfoData?.employmentSector }),
                            ...(employmentInfoData?.employmentType && { employmentType: employmentInfoData?.employmentType }),
                            ...(employmentInfoData?.description && { description: employmentInfoData?.description }),
                            ...(employmentInfoData?.documents && { documents: employmentInfoData?.documents }),
                            ...(employmentInfoData?.salary?.amount && { salary: employmentInfoData?.salary }),
                            ...(employmentInfoData?.selfEmployedDetails?.employeesCount && { selfEmployedDetails: employmentInfoData?.selfEmployedDetails })
                        }
                    })
                }
                updateProfile(EmploymentDetailsPayload, userProfile?.getUserId());
            }
        }
    }

    useEffect(() => {
        if (userProfile?.getCreatedAt) {
            const redirectionScreen = getRedirectScreen(userProfile, userProfile?.getRole());
            setCurrentSlide(redirectionScreen);
            const isCreatedBeforeRelease = isUserCreatedBeforeRelease(userProfile?.getRole(), userProfile?.getCreatedAt())
            const daysSinceRegistration = calculateDaysSinceRegistration(isCreatedBeforeRelease ? duration[userProfile?.getRole()]?.RELEASE_DATE : userProfile?.getCreatedAt());
            setHideDoLater(showForcedPopup(userProfile?.getRole(), daysSinceRegistration));
            const { showBanner, showPopup } = getDisplayStatus(userProfile?.getEducationalDetails && userProfile?.getEducationalDetails()[0]?.graduationDate, userProfile);
            setShowNudgeForPhase2(showBanner);
        }
    }, [userProfile]);

    useEffect(() => {
        if (prevIsProfileUpdating && !isProfileUpdating) {
            if (!updateProfileError && currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS) {
                const whatsappNotificationPayload: IUpdateSettingPayload = {
                    isCreateSettingData: false,
                    settingsData: {
                        userId: userProfile?.getUserId(),
                        notificationSettings: {
                            whatsAppOptIn: {
                                general: basicInfoData?.enableWhatsappNotification
                            }
                        }
                    }
                }
                updateUserSettings(whatsappNotificationPayload);
                if (userProfile?.getAspiringSectors()?.length === 0 &&
                    userProfile?.getEducationalDetails()?.length === 0 && userProfile?.getEmploymentDetails()?.length === 0) {
                    setCurrentSlide(POSTREGISTRATION_SCREENS.PLACEMENTDETAILS);
                } else {
                    onClickDoLater();
                }
            }
            else if (!updateProfileError && (currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS || (currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS
                && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS))) {
                onClickDoLater();
            }
            else if (updateProfileError) {
                displayErrorMessage(updateProfileError)
            }
        }
    }, [isProfileUpdating])

    const enableSubmitbtn = () => {
        var disableSubmit = false;
        if (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && Object.keys(presentStatusData)?.length === 0) {
            disableSubmit = true
        }
        if (currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS) {
            if (!basicInfoData?.phoneNumber?.phone || basicInfoData?.phoneNumber?.phone?.length === 0 ||
                (basicInfoData?.phoneNumber?.phone?.length > 0 && (FormHelper?.PhoneNumberValidation(basicInfoData?.phoneNumber))?.length > 0)) {
                disableSubmit = true
            }
        }
        if (currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) {
            if (presentStatusData?.isStudent === 1 && (!educationInfoData?.aspiringSector || educationInfoData?.aspiringSector?.length === 0 ||
                !educationInfoData?.degree || educationInfoData?.degree?.length === 0 || !educationInfoData?.graduationDate || educationInfoData?.graduationDate?.length === 0 ||
                educationInfoData?.instituteName?.length === 0)) {
                disableSubmit = true
            }
            if (presentStatusData?.isEmployed === 1 && (!employmentInfoData?.employerName || employmentInfoData?.employerName?.length === 0 ||
                !employmentInfoData?.employmentSector || employmentInfoData?.employmentSector?.length === 0
                || !employmentInfoData?.startDate || employmentInfoData?.startDate?.length === 0)) {
                disableSubmit = true
            }
            if ((presentStatusData?.isEmployed === 2 && presentStatusData?.isStudent === 2) && (!unEmployedDetail?.aspiringSector || unEmployedDetail?.aspiringSector?.length === 0)) {
                disableSubmit = true
            }
        }
        if (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) {
            if (presentStatusData?.presentStatus === presentStatusKeys.STILL_PURSUING && (!educationInfoData?.aspiringSector || educationInfoData?.aspiringSector?.length === 0 ||
                !educationInfoData?.degree || educationInfoData?.degree?.length === 0 || !educationInfoData?.graduationDate || educationInfoData?.graduationDate?.length === 0 ||
                educationInfoData?.instituteName?.length === 0)) {
                disableSubmit = true
            }
            if (presentStatusData?.presentStatus === presentStatusKeys.WORKING) {
                if (!employmentInfoData?.employmentType || employmentInfoData?.employmentType?.length === 0) {
                    disableSubmit = true
                }
                if (employmentInfoData?.employmentType === typeOfEmploymentKeys.SELF_EMPLOYED && (!employmentInfoData?.employerName || employmentInfoData?.employerName?.length === 0 ||
                    !employmentInfoData?.employmentSector || employmentInfoData?.employmentSector?.length === 0 || !employmentInfoData?.selfEmployedDetails?.employeesCount ||
                    !employmentInfoData?.selfEmployedDetails?.revenue?.amount || !employmentInfoData?.selfEmployedDetails?.revenue?.currency ||
                    !employmentInfoData?.selfEmployedDetails?.revenue?.duration)) {
                    disableSubmit = true
                }
                if (employmentInfoData?.employmentType === typeOfEmploymentKeys.FULL_TIME_JOB && (!employmentInfoData?.employerName || employmentInfoData?.employerName?.length === 0 ||
                    !employmentInfoData?.employmentSector || employmentInfoData?.employmentSector?.length === 0 || !employmentInfoData?.startDate ||
                    employmentInfoData?.startDate?.length === 0 || !employmentInfoData?.salary?.amount || !employmentInfoData?.salary?.currency ||
                    !employmentInfoData?.salary?.duration || !employmentInfoData?.documents || employmentInfoData?.documents?.length === 0)) {
                    disableSubmit = true
                }
                if (employmentInfoData?.employmentType === typeOfEmploymentKeys.OTHERS && (!employmentInfoData?.description || !employmentInfoData?.salary?.amount ||
                    employmentInfoData?.description?.length === 0 || !employmentInfoData?.salary?.currency || !employmentInfoData?.salary?.duration)) {
                    disableSubmit = true
                }
            }
            if (presentStatusData?.presentStatus === presentStatusKeys.OFFER_NOTJOINED) {
                if (!employmentInfoData?.employmentType || employmentInfoData?.employmentType?.length === 0) {
                    disableSubmit = true
                }
                if (employmentInfoData?.employmentType === typeOfEmploymentKeys.FULL_TIME_JOB && (!employmentInfoData?.employerName || employmentInfoData?.employerName?.length === 0 ||
                    !employmentInfoData?.employmentSector || employmentInfoData?.employmentSector?.length === 0 || !employmentInfoData?.startDate ||
                    employmentInfoData?.startDate?.length === 0 || !employmentInfoData?.salary?.amount || !employmentInfoData?.salary?.currency ||
                    !employmentInfoData?.salary?.duration || !employmentInfoData?.documents || employmentInfoData?.documents?.length === 0)) {
                    disableSubmit = true
                }
                if (employmentInfoData?.employmentType === typeOfEmploymentKeys.OTHERS && (!employmentInfoData?.description || !employmentInfoData?.salary?.amount ||
                    employmentInfoData?.description?.length === 0 || !employmentInfoData?.salary?.currency || !employmentInfoData?.salary?.duration)) {
                    disableSubmit = true
                }
            }
            if (presentStatusData?.presentStatus === presentStatusKeys.LOOKING_FOR_JOB) {
                disableSubmit = false
            }
        }
        return disableSubmit;
    }

    const showFooterBtn = () => {
        var showFooterBtn = false;
        if (currentSlide && currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS) {
            showFooterBtn = true;
        }
        if (currentSlide && currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS &&
            ((presentStatusData?.isEmployed === 1 && (presentStatusData.isStudent === 1 || presentStatusData.isStudent === 2))
                || (presentStatusData?.isStudent === 1 && (presentStatusData.isEmployed === 1 || presentStatusData.isEmployed === 2))
                || (presentStatusData.isEmployed === 2 && presentStatusData.isStudent === 2))) {
            showFooterBtn = true;
        }
        if (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) {
            showFooterBtn = true;
        }
        return showFooterBtn;
    }

    if (!currentSlide) {
        <Shimmer />
    }
    if (isProfileUpdating) {
        <WFSpinner />
    }

    return (
        <AuthForm formContainerStyle="profileCompletion_formData" image={SignIn} showLogo={false} showText={(currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS)}>
            <Text
                textType={TextType.display2}
                fontFamilyVariant={FontFamilyVariant.bold1}
                className={"heading"}
                text={translate(t, dashboardMessages.completeProfileScreenHeading)}
            />
            <div className="stepper_container">
                <div className={`stepper ${currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS && 'active_stepper'}`} />
                <div className={`stepper ${(currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) || (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) && 'active_stepper'}`} />
            </div>
            <form onSubmit={() => { }}>
                <div className="profile_complete_data_container">
                    {currentSlide === POSTREGISTRATION_SCREENS.BASICDETAILS && <div className="basicInfo_container">
                        <BasicInfo basicInfo={basicInfoData} setBasicInfo={setbasicInfoData} />
                    </div>}
                    {currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS && <div className="education_employment_container">
                        <EducationEmploymentInfo
                            setPresentStatusData={setPresentStatusData}
                            setEducationInfoData={setEducationInfoData}
                            setEmploymentInfoData={setEmploymentInfoData}
                            setUnEmployedDetail={setUnEmployedDetail}
                            presentStatusData={presentStatusData}
                            educationalInfoData={educationInfoData}
                            employmentInfoData={employmentInfoData}
                            unEmployedDetail={unEmployedDetail}
                        />
                    </div>}
                    {(currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) &&
                        <div>
                            <EducationEmploymentInfoPhase2
                                setPresentStatusData={setPresentStatusData}
                                setEducationInfoData={setEducationInfoData}
                                setEmploymentInfoData={setEmploymentInfoData}
                                setUnEmployedDetail={setUnEmployedDetail}
                                presentStatusData={presentStatusData}
                                educationalInfoData={educationInfoData}
                                employmentInfoData={employmentInfoData}
                                unEmployedDetail={unEmployedDetail}
                            />
                        </div>}
                </div>
            </form>
            {showFooterBtn() && <div className="footer_wrapper">
                <PrimaryButton
                    type={"primary"}
                    htmlType={"button"}
                    className="next_button"
                    onClick={() => onClickCompleteProfile()}
                    loading={isProfileUpdating}
                    disabled={enableSubmitbtn()}
                >
                    {currentSlide === POSTREGISTRATION_SCREENS.PLACEMENTDETAILS ||
                        (currentSlide !== POSTREGISTRATION_SCREENS.BASICDETAILS && currentSlide !== POSTREGISTRATION_SCREENS.PLACEMENTDETAILS) ? translate(t, dashboardMessages.submit) :
                        translate(t, dashboardMessages.next)}
                </PrimaryButton>
                {(!hideDoLater || showNudgeForPhase2) && <div className="doLater_btn_wrapper" onClick={onClickDoLater}>
                    <Text
                        textType={TextType.h8}
                        fontFamilyVariant={FontFamilyVariant.regular}
                        className={"doLater_text"}
                        text={translate(t, dashboardMessages.iWillDoLater)}
                    />
                </div>
                }
            </div>
            }
        </AuthForm>
    );
}

export default ProfileCompletion;
